import React from 'react';

/**
 * ButtonComponent — Shopify/shadcn-style reusable button for the entire admin panel.
 *
 * Props:
 *   type      – HTML button type (default: 'button')
 *   onClick   – click handler
 *   disabled  – disables the button
 *   loading   – shows a spinner and disables interaction
 *   variant   – 'primary' | 'secondary' | 'ghost' | 'link' | 'danger' | 'success' | 'outline' | 'selectbutton'
 *   size      – 'sm' | 'md' (default) | 'lg'
 *   className – extra Tailwind/CSS classes merged onto the button
 *   icon      – optional JSX icon rendered before children
 *   children  – button label content
 *   tabIndex  – HTML tabIndex
 */
const ButtonComponent = ({
  type = 'button',
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'md',
  className = '',
  icon,
  children,
  tabIndex,
  ...props
}) => {
  // ── Size styles ──────────────────────────────────────────────────
  const sizeStyles = {
    sm: 'h-8 px-3 text-xs gap-1.5 rounded-md font-medium',
    md: 'h-[36px] px-3.5 text-sm gap-2 rounded-lg font-medium',
    lg: 'h-10 px-4 text-sm gap-2 rounded-lg font-medium',
  };

  // ── Variant styles ───────────────────────────────────────────────
  const variantStyles = {
    primary:
      'bg-[var(--btn-bg-primary)] text-white border border-transparent hover:bg-[var(--bg-btn-hover)] active:scale-[0.98] shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed',
    secondary:
      'bg-[var(--secondary-bg-color)] text-[var(--primary-text-color)] border border-[var(--border-color)] hover:bg-[var(--focus-tab-color)] hover:border-[var(--focus-border-color)] active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
    outline:
      'bg-transparent text-[var(--primary-text-color)] border border-[var(--border-color)] hover:bg-[var(--focus-tab-color)] hover:border-[var(--focus-border-color)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed',
    ghost:
      'bg-transparent text-[var(--primary-text-color)] border border-transparent hover:bg-[var(--focus-tab-color)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed',
    link: 'bg-transparent text-[var(--btn-bg-primary)] border-0 underline-offset-4 hover:underline p-0 disabled:opacity-50 disabled:cursor-not-allowed',
    danger:
      'bg-[var(--color-danger)] text-white border border-transparent hover:opacity-90 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
    success:
      'bg-[var(--color-success)] text-white border border-transparent hover:opacity-90 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
    selectbutton:
      'bg-[var(--secondary-bg-color)] text-[var(--primary-text-color)] border border-[var(--border-color)] hover:bg-[var(--focus-tab-color)] hover:border-[var(--focus-border-color)] active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-medium !text-[13px] rounded-[var(--radius-sm)] shadow-[var(--shadow-card)]',
  };

  const focusRingStyle = variant === 'link' 
    ? 'focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none border-none outline-none ring-0' 
    : 'focus:ring-2 focus:ring-transparent';

  const baseStyle =
    `inline-flex items-center justify-center transition-all duration-150 focus:outline-none ${focusRingStyle} select-none whitespace-nowrap`;

  const appliedSize = sizeStyles[size] || sizeStyles.md;
  const appliedVariant = variantStyles[variant] || variantStyles.primary;

  // For link variant skip the size padding
  const finalSize = variant === 'link' ? 'text-sm gap-1.5 font-medium' : appliedSize;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      tabIndex={tabIndex}
      className={`${baseStyle} ${finalSize} ${appliedVariant} ${className}`}
      {...props}
    >
      {loading ? (
        <div className='flex items-center justify-center gap-2'>
          <div className='animate-spin inline-block w-3.5 h-3.5 border-[2px] border-current border-t-transparent rounded-full opacity-80' />
          {children && <span className='inline-flex items-center justify-center gap-1.5 leading-none'>{children}</span>}
        </div>
      ) : (
        <>
          {icon && <span className='inline-flex items-center justify-center shrink-0 text-current'>{icon}</span>}
          {children && <span className='inline-flex items-center justify-center gap-1.5 leading-none'>{children}</span>}
        </>
      )}
    </button>
  );
};

export default ButtonComponent;